import { styled } from "styled-components";

import { CheckIcon, FollowIcon, Image, Spinner } from "common/components";
import type { ImageCollection } from "common/types/imageCollection";
import { useTopicFollowing } from "domains/topic/components/useTopicFollowing";

const StyledContainer = styled.div`
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.fillPrimary};
  font-weight: 500;
`;

const StyledImage = styled(Image)`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  margin-right: 15px;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
`;

const StyledFollowIcon = styled(FollowIcon)`
  color: ${({ theme }) => theme.colors.actionIcon};
  opacity: 0.8;
  cursor: pointer;
  width: 40px;
  height: 40px;
  transition: 0.3s all;
  &:hover {
    opacity: 1;
  }
`;

const StyledUnfollow = styled.div`
  transition: 0.3s all;
  display: flex;
  color: ${({ theme }) => theme.colors.backgroundPrimary};
  background-color: ${({ theme }) => theme.colors.actionIcon};
  opacity: 0.8;
  border-radius: 20px;
  cursor: pointer;
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;
  &:hover {
    opacity: 1;
  }
`;

const StyledUnfollowIcon = styled(CheckIcon)`
  width: 12px;
  height: 12px;
  fill: none;
`;

const StyledSpinner = styled(Spinner)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.blue};
`;

type ManageTopicItemProps = {
  id: string;
  images: ImageCollection | null;
  isFollowing: boolean;
  isFollowingUpdating: boolean | undefined;
  name: string;
};

export const ManageTopicItem = ({ id, name, images, isFollowing }: ManageTopicItemProps) => {
  const { isFollowingUpdating, toggleFollow } = useTopicFollowing({
    topicId: id,
    topicName: name,
    isFollowing,
    pageForTracking: "Monitor",
  });

  const getIcon = () => {
    if (isFollowingUpdating) return <StyledSpinner />;

    if (isFollowing) {
      return (
        <StyledUnfollow onClick={() => void toggleFollow()}>
          <StyledUnfollowIcon />
        </StyledUnfollow>
      );
    }

    return <StyledFollowIcon onClick={() => void toggleFollow()} />;
  };

  return (
    <StyledContainer>
      <StyledRow>
        <StyledImage src={images?.x_small} alt={name} />
        <StyledText>{name}</StyledText>
      </StyledRow>
      {getIcon()}
    </StyledContainer>
  );
};
